import {
  call, put, select, takeLatest,
} from 'redux-saga/effects';
import { FastCheckoutActionTypes, FastCheckoutLog } from './fastcheckout.types';
import { isBRServer } from '@/utils/Validators/validation';
import { COUNTRY, WHMCS_URL } from '@/config';
import { api } from '@/utils/api';
import { commonActions } from '../common';
import { locale } from '@/utils/locale';
import logger from '@/utils/logger';
import fastCheckoutAssembly from './fastcheckout.assembly';
import fastCheckoutActions from './fastcheckout.actions';

const maskRequest = (request = {}) => {
  const { Authorization, 'x-service-token': xServiceToken, ...otherHeaders } = request.headers || {};

  const maskString = (value) => {
    if (typeof value !== 'string') return value;
    if (value.length < 6) return '*'.repeat(value.length);
    return value.replace(/.(?=.{5})/g, '*');
  };

  const maskedHeaders = {
    ...otherHeaders,
    ...(Authorization && {
      Authorization: maskString(Authorization),
    }),
    ...(xServiceToken && {
      'x-service-token': maskString(xServiceToken),
    }),
  };

  return {
    ...request,
    headers: maskedHeaders,
  };
};

const getElapsedSeconds = (initDuration) => {
  const now = new Date().getTime();
  if (!initDuration) {
    return now;
  }

  if (typeof initDuration !== 'number') {
    return;
  }

  return (now - initDuration) / 1000;
};

function* requestFastCheckoutProduct(action) {
  const { coupon, pid, billingCycle } = action.payload;
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${WHMCS_URL}/api/v3/fast_checkout_product/${pid}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
      brand,
    },
    data: {
      coupon_code: coupon,
      cycle: billingCycle,
    },
  };

  const maskedRequest = maskRequest(request);
  const initDuration = getElapsedSeconds();

  yield put(fastCheckoutActions.loading(true));

  try {
    const response = yield call(api.whmcs.post, request);
    const parsedProduct = fastCheckoutAssembly.receiveFastCheckoutProduct(response);

    const message = {
      caller: 'onLoad',
      request: maskedRequest,
      response: (typeof response === 'object' && 'data' in response) ? response.data : response,
      performance: {
        duration: getElapsedSeconds(initDuration),
      },
    };
    logger.info(FastCheckoutLog.FASTCHECKOUT_CALCULATE_SUCCESS, message);

    yield put(fastCheckoutActions.product.set(parsedProduct));
    yield put(fastCheckoutActions.product.success());
  } catch (error) {
    const messageContext = {
      ...error,
      request: maskedRequest,
      performance: {
        duration: getElapsedSeconds(initDuration),
      },
    };
    logger.error(FastCheckoutLog.FASTCHECKOUT_CALCULATE_FAILURE, messageContext);

    yield put(fastCheckoutActions.product.failure());
    yield put(commonActions.notifications.set({
      label: locale('fastCheckout.errorToast'),
      type: 'error',
    }));
  }
  yield put(fastCheckoutActions.loading(false));
}

function* fastCheckoutPurchase(action) {
  const {
    discount, product, paymentMethod, onSuccessCallback,
  } = action.payload;
  const { token, jwt: jwtToken } = yield select(state => state.auth);

  yield put(fastCheckoutActions.purchase.setError(false));

  const request = {
    url: `${WHMCS_URL}/api/v3/fastcheckout`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
      brand: COUNTRY,
    },
    data: {
      discount,
      product,
      paymentMethod,
      domain: [], // Note: Not implemented yet
      fingerprint: '',
    },
  };

  const maskedRequest = maskRequest(request);
  const initDuration = getElapsedSeconds();

  yield put(fastCheckoutActions.loading(true));

  try {
    const response = yield call(api.whmcs.post, request);
    yield put(fastCheckoutActions.purchase.success());

    const messageContext = {
      request: maskedRequest,
      response: (typeof response === 'object' && 'data' in response) ? response.data : response,
      performance: {
        duration: getElapsedSeconds(initDuration),
      },
    };
    logger.info(FastCheckoutLog.FASTCHECKOUT_PROCESS_SUCCESS, messageContext);

    onSuccessCallback && onSuccessCallback();
  } catch (error) {
    const messageContext = {
      ...error,
      request: maskedRequest,
      performance: {
        duration: getElapsedSeconds(initDuration),
      },
    };
    logger.error(FastCheckoutLog.FASTCHECKOUT_PROCESS_FAILURE, messageContext);

    yield put(fastCheckoutActions.purchase.setError(true));
  }
  yield put(fastCheckoutActions.loading(false));
}

function* sagas() {
  yield takeLatest(FastCheckoutActionTypes.REQUEST_FAST_CHECKOUT_PRODUCT, requestFastCheckoutProduct);
  yield takeLatest(FastCheckoutActionTypes.REQUEST_FAST_CHECKOUT_PURCHASE, fastCheckoutPurchase);
}

export default sagas;
